/**
 * HTTP-клиент.
 *
 * `initData` подставляется в каждый запрос: бэкенд определяет пользователя
 * только из подписи, поэтому идентификатор никогда не передаётся телом
 * или параметром — подменить его было бы тривиально.
 */
import { retrieveRawInitData } from '@telegram-apps/sdk-react';
import type { z } from 'zod';

import { config } from '@/shared/config/env';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly code?: string,
  ) {
    super(detail);
    this.name = 'ApiError';
  }

  /** Подпись устарела — клиенту следует перезапросить initData, а не показывать ошибку. */
  get isExpiredAuth(): boolean {
    return this.status === 401 && this.code === 'expired';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export class ContractError extends Error {
  constructor(path: string, issues: string) {
    super(`Ответ ${path} не соответствует ожидаемому формату: ${issues}`);
    this.name = 'ContractError';
  }
}

function authHeader(): string {
  const raw = retrieveRawInitData();
  if (!raw) {
    throw new ApiError(401, 'Приложение открыто вне Telegram');
  }
  return `tma ${raw}`;
}

function parseAuthError(response: Response): string | undefined {
  // Telegram-специфичный признак приходит в WWW-Authenticate:
  // `tma error="expired"`.
  const header = response.headers.get('WWW-Authenticate') ?? '';
  return /error="([^"]+)"/.exec(header)?.[1];
}

async function request<S extends z.ZodType>(
  method: 'GET' | 'POST',
  path: string,
  schema: S,
  body?: unknown,
  { auth = true }: { auth?: boolean } = {},
): Promise<z.infer<S>> {
  const headers: Record<string, string> = auth ? { Authorization: authHeader() } : {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers,
    body: body === undefined ? null : JSON.stringify(body),
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const payload: unknown = await response.json();
      if (payload && typeof payload === 'object' && 'detail' in payload) {
        detail = String((payload as { detail: unknown }).detail);
      }
    } catch {
      // Тело может быть не-JSON (например, ошибка прокси) — не страшно,
      // текста статуса достаточно.
    }
    throw new ApiError(response.status, detail, parseAuthError(response));
  }

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    throw new ContractError(path, parsed.error.issues.map((i) => i.path.join('.')).join(', '));
  }
  return parsed.data;
}

export function apiGet<S extends z.ZodType>(path: string, schema: S): Promise<z.infer<S>> {
  return request('GET', path, schema);
}

/** Запрос без авторизации — только для публичных данных (режим просмотра). */
export function apiGetPublic<S extends z.ZodType>(path: string, schema: S): Promise<z.infer<S>> {
  return request('GET', path, schema, undefined, { auth: false });
}

export function apiPost<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
): Promise<z.infer<S>> {
  return request('POST', path, schema, body);
}
